/**
 * Respuestas guardadas (canned responses) — picker para el composer del inbox.
 *
 * Botón que abre un popover con buscador + lista de respuestas del cliente.
 * Al elegir una, inserta su texto en el composer (reemplazando {nombre} por el
 * nombre del usuario) y registra el uso. Permite agregar y borrar respuestas.
 */
import { useMemo, useRef, useState } from "react";
import { MessageSquareText, Plus, Search, Trash2, X } from "lucide-react";
import {
  useSavedReplies,
  useCreateSavedReply,
  useDeleteSavedReply,
  useMarkSavedReplyUsed,
  type SavedReply,
} from "@/hooks/use-saved-replies";
import { cn } from "@/lib/cn";

function applyName(body: string, userName: string | null | undefined): string {
  const name = (userName ?? "").trim() || "";
  return body.replace(/\{nombre\}/gi, name);
}

export function SavedRepliesPicker({
  clientSlug,
  userName,
  onInsert,
  disabled,
}: {
  clientSlug: string | undefined;
  userName: string | null | undefined;
  onInsert: (text: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data: replies } = useSavedReplies(clientSlug);
  const del = useDeleteSavedReply(clientSlug);
  const markUsed = useMarkSavedReplyUsed(clientSlug);

  const filtered = useMemo(() => {
    if (!replies) return [];
    const q = query.trim().toLowerCase();
    if (!q) return replies;
    return replies.filter(
      (r) =>
        r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q),
    );
  }, [replies, query]);

  function pick(r: SavedReply) {
    onInsert(applyName(r.body, userName));
    markUsed.mutate({ id: r.id });
    setOpen(false);
    setQuery("");
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title="Respuestas guardadas"
        className={cn(
          "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border font-sans text-xs font-medium transition-colors",
          "border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary",
          "disabled:opacity-40 disabled:cursor-not-allowed",
        )}
      >
        <MessageSquareText size={15} strokeWidth={1.75} />
        <span className="hidden sm:inline">Respuestas</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 bottom-full mb-2 z-30 w-[360px] max-w-[90vw] rounded-xl border border-default bg-surface-1 shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-3.5 pt-3 pb-2">
              <span className="font-sans text-sm font-semibold text-text-primary">
                Respuestas guardadas
              </span>
              <button
                type="button"
                onClick={() => setAdding((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-sans font-medium text-magenta hover:opacity-80"
              >
                <Plus size={13} strokeWidth={2} />
                Agregar
              </button>
            </div>

            {adding && (
              <AddForm
                clientSlug={clientSlug}
                onDone={() => setAdding(false)}
              />
            )}

            {/* Search */}
            <div className="px-3 pb-2">
              <div className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-surface-2 border border-default">
                <Search size={14} strokeWidth={1.75} className="text-text-muted shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar…"
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none font-sans text-sm text-text-primary placeholder:text-text-muted"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-[320px] overflow-y-auto border-t border-subtle">
              {!replies ? (
                <div className="px-4 py-6 text-sm text-text-muted text-center">Cargando…</div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-6 text-sm text-text-muted text-center">
                  {replies.length === 0
                    ? "Sin respuestas guardadas. Agregá la primera."
                    : "Nada coincide con la búsqueda."}
                </div>
              ) : (
                <ul className="flex flex-col">
                  {filtered.map((r) => (
                    <li
                      key={r.id}
                      className="group border-b border-subtle last:border-b-0"
                    >
                      <div className="flex items-start gap-2 px-3.5 py-2.5 hover:bg-surface-2 transition-colors">
                        <button
                          type="button"
                          onClick={() => pick(r)}
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="font-sans text-sm font-semibold text-text-primary truncate">
                            {r.title}
                          </div>
                          <div className="font-sans text-xs text-text-secondary line-clamp-2 leading-snug mt-0.5">
                            {r.body}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`¿Borrar la respuesta "${r.title}"?`)) {
                              del.mutate({ id: r.id });
                            }
                          }}
                          title="Borrar"
                          className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center w-7 h-7 rounded-md text-text-muted hover:text-error-text hover:bg-error-bg transition-all shrink-0"
                        >
                          <Trash2 size={13} strokeWidth={1.75} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-3.5 py-2 border-t border-subtle">
              <span className="font-sans text-2xs text-text-muted">
                Tip: usá <span className="font-mono text-text-secondary">{"{nombre}"}</span> y se
                reemplaza por el nombre del usuario.
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AddForm({
  clientSlug,
  onDone,
}: {
  clientSlug: string | undefined;
  onDone: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const create = useCreateSavedReply();

  function submit() {
    if (!clientSlug || !title.trim() || !body.trim() || create.isPending) return;
    create.mutate(
      { clientSlug, title: title.trim(), body: body.trim() },
      {
        onSuccess: () => {
          setTitle("");
          setBody("");
          onDone();
        },
      },
    );
  }

  return (
    <div className="mx-3 mb-2 p-3 rounded-lg bg-surface-2 border border-default flex flex-col gap-2">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Nombre (ej: Bienvenida)"
        maxLength={120}
        autoFocus
        className="h-8 px-2.5 rounded-md bg-surface-1 border border-default font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-magenta"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Texto de la respuesta… (podés usar {nombre})"
        rows={3}
        maxLength={4000}
        className="px-2.5 py-1.5 resize-none rounded-md bg-surface-1 border border-default font-sans text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-magenta"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-text-secondary hover:bg-surface-1 font-sans text-xs font-medium transition-colors"
        >
          <X size={12} strokeWidth={2} />
          Cancelar
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!title.trim() || !body.trim() || create.isPending}
          className="inline-flex items-center h-7 px-3 rounded-md bg-magenta text-white font-sans text-xs font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {create.isPending ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}
